import { z } from "zod";

import {
    type HallidayPaymentsConfig,
    type PaymentsWidgetSDKParams,
    type DangerousOverrides,
    type OrderStatus,
    type OrderNotification,
    type HallidayEvent,
    type HallidayEventHandler,
    type HallidayErrorSource,
    type HallidayRuntimeError,
    type HallidaySnapshot,
    type WidgetLoadDiagnostics,
    PaymentSession,
    WidgetLoadError,
    WidgetLoadFailureReason,
} from "./types";
import { normalizeConfig } from "./modules/normalizeConfig";
import {
    createWidgetState,
    type WidgetContext,
    subscribe as ctxSubscribe,
    getSnapshot as ctxGetSnapshot,
    setReactiveState as ctxSetReactiveState,
    getWindowOrigin,
    getContentWindow,
    isInitialized,
    isPreloaded,
    getState,
    setState,
    mergeParams,
    mergeAndStoreConfig,
    stripFunctions,
    resetAll,
} from "./modules/state";
import { runPipeline } from "./modules/pipeline";
import { validateParams, validateOverrides, remapOverrides, computeWindowOrigin } from "./modules/validateAndPrepare";
import { hideWidget, ensureVisible, destroyDisplay } from "./modules/display";
import { cleanMessageHandlers, sendUpdateParams } from "./modules/messaging";
import { createEmbedSkeleton, removeEmbedSkeleton } from "./modules/skeleton";
import { onDomReady } from "./modules/domReady";
import { registerInstance, deregisterInstance } from "./modules/instanceRegistry";
import { devError } from "./modules/devWarn";

/** Fixed readiness timeout — not configurable. */
const READY_TIMEOUT_MS = 30_000;

/** Deposit open input — the session minus the method-injected `direction`. */
const DepositSession = PaymentSession.omit({ direction: true });
/** Withdraw open input — no `direction` (injected) and no `fundingAddress` (meaningless for a single withdraw funder). */
const WithdrawSession = PaymentSession.omit({ direction: true, fundingAddress: true });

/** Diagnostics stub for the synthesized timeout error (the load monitor never fired). */
const TIMEOUT_DIAGNOSTICS: WidgetLoadDiagnostics = {
    onloadFired: false,
    cspViolationDetected: false,
    cspBlockedURI: null,
    iframeInDOM: false,
    resourceTimingEntryExists: false,
    elapsedMs: READY_TIMEOUT_MS,
};

/** The flat engine config the class owns — `PaymentsWidgetSDKParams` plus the typed overrides bag. */
type StoredConfig = Partial<PaymentsWidgetSDKParams> & { dangerousOverrides?: DangerousOverrides };

/**
 * Re-apply required-with-default fields that landed absent (today only `outputs`). Called by BOTH the
 * constructor and `updateConfig` so the default rule lives in exactly one place — no duplicated logic and
 * no "default is NOT re-injected" asymmetry. Only fills when the field is absent, so it never clobbers a
 * user-set value (including an explicit empty array). Mutates `config` in place.
 */
const applyRequiredDefaults = (config: StoredConfig): void => {
    if (config.outputs === undefined) {
        config.outputs = [];
    }
};

type ReadyCycle = {
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout> | null;
};

/**
 * The single instance-based SDK entry point. One object owns the config, state, render pipeline,
 * messaging, lifecycle, and every entry point. Construction is pure and SSR-safe; warming is automatic.
 *
 * **All methods are arrow-bound class fields** so they can be detached safely — React destructuring,
 * `onClick={openDeposit}`, and `useSyncExternalStore(core.subscribe, core.getSnapshot)`. A detached
 * prototype method would lose `this` and throw.
 */
export class HallidayPayments {
    /**
     * Monotonic per-process counter for the bare-instance DOM-key fallback — guarantees two
     * `{ apiKey }`-only instances (no `instanceId`/`targetElementId`) never share a key or DOM id.
     */
    private static _seq = 0;

    /** Per-instance engine context (DOM ids, state, generation guard, reactive store). */
    private readonly _ctx: WidgetContext;
    /** Canonical flat config (validated, default-baked) + injected bridge callbacks. */
    private _config: StoredConfig;
    /** Latest-wins host wallet-connect hook; the registered handler always calls the current value. */
    private _userConnect: (() => void) | undefined;
    /** Latest-wins host fee-sponsorship signer; the stable proxy always calls the current value. */
    private _userGenerateGrant: ((payload: string) => Promise<string>) | undefined;
    /** Source tag for the next non-load runtime error. */
    private _lastSource: HallidayErrorSource = "load";
    /** Whether this instance has lazily joined the dev registry (first DOM touch). */
    private _registered = false;
    /** Memoized readiness promise for the current load cycle. */
    private _ready: ReadyCycle | null = null;
    /** True once the current cycle rejected — a genuine (re)load re-arms readiness. */
    private _readyFailed = false;
    /** True once `ready()` has been obtained — half of the "is an error channel wired?" check. */
    private _readyAwaited = false;

    private readonly _listeners: Record<HallidayEvent, Set<(arg?: unknown) => void>> = {
        status: new Set(),
        error: new Set(),
        close: new Set(),
    };

    constructor(config: HallidayPaymentsConfig) {
        const normalized = normalizeConfig(config);
        // instanceId/autoPreload are class-level knobs — consumed here, never fed to the widget.
        const { instanceId, autoPreload, ...flat } = normalized;
        this._userConnect = flat.onConnectWallet;
        this._userGenerateGrant = flat.generateFeeSponsorshipGrant;

        const stored: StoredConfig = { ...flat };
        // Bake the deposit.outputs default BEFORE validation — keeps flat `outputs` required for the
        // pipeline while the public `deposit.outputs` stays optional. NOT applied in normalizeConfig. Shared
        // with updateConfig via applyRequiredDefaults so the default rule lives in exactly one place.
        applyRequiredDefaults(stored);
        // Install the connect proxy ONLY when the host actually supplied a handler, so `hasConnect`
        // (derived from typeof _config.onConnectWallet) is honest. A later `updateConfig({ onConnectWallet })`
        // still needs no re-wire — the proxy is stable and reads `_userConnect` at call time.
        if (this._userConnect) stored.onConnectWallet = this._connectProxy;
        else delete stored.onConnectWallet;
        // Same invariant for the fee-sponsorship grant proxy: install ONLY when the host supplied a callback,
        // so `hasFeeSponsorship` (derived from typeof _config.generateFeeSponsorshipGrant) stays honest. The
        // stable proxy reads `_userGenerateGrant` at call time, so a later swap needs no re-wire.
        if (this._userGenerateGrant) stored.generateFeeSponsorshipGrant = this._grantProxy;
        else delete stored.generateFeeSponsorshipGrant;
        // Inject the remaining internal bridges that wire events / the reactive store / ready().
        stored.onReady = this._handleWidgetReady;
        stored.onStatus = this._handleStatus;
        stored.onNotifications = this._handleNotifications;
        stored.onError = this._handleError;

        // Synchronous structural validation — throws on missing/non-string apiKey or malformed shapes.
        validateParams(stored as PaymentsWidgetSDKParams);
        validateOverrides(stored.dangerousOverrides ?? {});
        this._config = stored;

        // DOM-id namespace, resolved once and stable for the lifetime: instanceId → targetElementId →
        // a unique `auto-N` so two bare `{ apiKey }` instances never share a key/DOM id. Stored as `ctx.key`.
        const domKey = instanceId ?? flat.targetElementId ?? `auto-${++HallidayPayments._seq}`;
        this._ctx = createWidgetState(domKey);
        this._ctx.onClose = this._handleClose;

        // Schedule (do NOT run) the auto-warm through the DOM-ready gate. `autoPreload: false` opts out
        // (used by the React Provider to defer warming to its mount effect).
        if (autoPreload !== false) {
            this.preload();
        }
    }

    // ====================================================================
    // Lifecycle
    // ====================================================================

    /**
     * Resolves on the widget's ready message (`WIDGET_READY`/`HALLIDAY_READY`); rejects with a
     * `WidgetLoadError` on load failure, async open-time failure, or the fixed 30s timeout. Memoized per
     * load cycle — `destroy()` clears it and the next genuine (re)load installs a fresh one; a no-reload
     * `updateConfig` does not reset it.
     */
    ready = (): Promise<void> => {
        this._readyAwaited = true;
        if (this._ready) return this._ready.promise;
        if (ctxGetSnapshot(this._ctx).isReady) return Promise.resolve();
        this._armReadyCycle();
        return this._ready!.promise;
    };

    /** Tear down listeners, iframe, DOM, the memoized `ready()`, and reset state. */
    destroy = (): void => {
        deregisterInstance(this._registryKey(), this);
        this._registered = false;
        cleanMessageHandlers(this._ctx);
        destroyDisplay(this._ctx);
        // resetAll bumps the generation (discards in-flight resolutions), resets state, and resets the store.
        resetAll(this._ctx);
        if (this._ready) {
            this._ready.reject(new Error("HallidayPayments instance destroyed"));
            if (this._ready.timer) clearTimeout(this._ready.timer);
            this._ready = null;
        }
        this._readyFailed = false;
    };

    // ====================================================================
    // Entry points (zero-arg — validate stored config, then show)
    // ====================================================================

    /**
     * Open the deposit/payment flow (EMBED if `targetElementId` is set, else MODAL). Opens on defaults.
     *
     * A `session` is optional: when passed it is validated synchronously (throws at the call site on a
     * **structurally malformed** session) and delivered to the widget with `direction:"inbound"` injected.
     * It never throws for an incomplete-but-valid or absent session. A zero-arg `openDeposit()` sends **no**
     * session, preserving show-only re-show / in-progress state.
     */
    openDeposit = (session?: z.input<typeof DepositSession>): void => {
        ctxSetReactiveState({ error: null }, this._ctx);
        this._lastSource = "openDeposit";
        // Build + validate BEFORE the DOM-ready gate so a malformed session throws synchronously at the
        // call site. Only build when the caller passed one — a bare session would reset the flow.
        const depositSession =
            session === undefined ? undefined : { ...DepositSession.parse(session), direction: "inbound" as const };
        this._gatedEffect(() => {
            this._beginLoadCycle();
            this._maybeCreateSkeleton();
            runPipeline(this._config, { show: true, session: depositSession }, this._ctx).catch((err) => {
                removeEmbedSkeleton(this._ctx);
                this._reportError(err, "openDeposit");
            });
        });
    };

    /**
     * Open the withdrawal flow. Throws synchronously when the stored config is incomplete or
     * when a passed `session` is structurally malformed. Always emits an **outbound** session (even zero-arg)
     * — the replacement for the retired `sessionType:"withdraw"` signal; `direction:"outbound"` is injected.
     */
    openWithdrawal = (session?: z.input<typeof WithdrawSession>): void => {
        ctxSetReactiveState({ error: null }, this._ctx);
        this._lastSource = "openWithdrawal";

        // Deterministic, computable-from-stored-config validation → throw at the call site.
        const withdrawInputs = this._resolveWithdrawInputs();
        if (!this._config.withdrawFunder) {
            throw new Error(
                "HallidayPayments.openWithdrawal: `withdrawal.funder` is required — there is no fallback " +
                    "to deposit funders or owner.",
            );
        }

        // Build + validate the outbound session synchronously (before the gate) — a zero-arg call still
        // sends a bare outbound session. A malformed session throws at the call site.
        const withdrawSession = { ...WithdrawSession.parse(session ?? {}), direction: "outbound" as const };

        const withdrawConfig: StoredConfig = { ...this._config, withdrawInputs };
        this._gatedEffect(() => {
            this._beginLoadCycle();
            this._maybeCreateSkeleton();
            runPipeline(withdrawConfig, { show: true, session: withdrawSession }, this._ctx).catch((err) => {
                removeEmbedSkeleton(this._ctx);
                this._reportError(err, "openWithdrawal");
            });
        });
    };

    /** Show the existing widget on the activity/history view. Throws synchronously if no widget exists. */
    openActivity = (): void => {
        ctxSetReactiveState({ error: null }, this._ctx);
        if (!isInitialized(this._ctx)) {
            throw new Error(
                "HallidayPayments.openActivity: no widget to navigate — open a flow (openDeposit/openWithdrawal) first.",
            );
        }
        this._gatedEffect(() => {
            const windowOrigin = getWindowOrigin(this._ctx) ?? "";
            const nav = { targetView: "history" } as const;
            if (ctxGetSnapshot(this._ctx).isReady) {
                // Widget is ready — send navigation immediately so the right view is shown before visible.
                sendUpdateParams(getContentWindow(this._ctx), nav, windowOrigin);
            } else {
                // Widget is initializing — stash for the pipeline's onReady flush (avoids a dropped message).
                const prev = getState(this._ctx).pendingUpdateParams;
                const config = prev ? { ...prev.config, ...nav } : { ...nav };
                setState({ pendingUpdateParams: { config, windowOrigin } }, this._ctx);
            }
            setTimeout(() => ensureVisible(this._ctx), 100);
        });
    };

    /** Show the existing widget on the history view with a specific order targeted. Throws synchronously if no widget exists. */
    openOrder = (paymentId: string): void => {
        ctxSetReactiveState({ error: null }, this._ctx);
        if (!isInitialized(this._ctx)) {
            throw new Error(
                "HallidayPayments.openOrder: no widget to navigate — open a flow (openDeposit/openWithdrawal) first.",
            );
        }
        this._gatedEffect(() => {
            const windowOrigin = getWindowOrigin(this._ctx) ?? "";
            const nav = { targetView: "history", selectedPaymentId: paymentId } as const;
            if (ctxGetSnapshot(this._ctx).isReady) {
                // Widget is ready — send navigation immediately.
                sendUpdateParams(getContentWindow(this._ctx), nav, windowOrigin);
            } else {
                // Widget is initializing — stash for the pipeline's onReady flush.
                const prev = getState(this._ctx).pendingUpdateParams;
                const config = prev ? { ...prev.config, ...nav } : { ...nav };
                setState({ pendingUpdateParams: { config, windowOrigin } }, this._ctx);
            }
            setTimeout(() => ensureVisible(this._ctx), 100);
        });
    };

    /** Hide the widget (kept alive for reuse). */
    close = (): void => {
        // hideWidget flips isOpen:false and fires the close hook → our "close" event. No DOM-ready gate
        // needed: it only touches the existing iframe (a no-op when none exists), never `document`.
        hideWidget(this._ctx);
    };

    // ====================================================================
    // Config
    // ====================================================================

    /** Merge new params and push them to the live widget (never shows). Nested → flat → shallow-merge. */
    updateConfig = (partial: Partial<HallidayPaymentsConfig>): void => {
        const normalized = normalizeConfig(partial);
        const { instanceId: _instanceId, autoPreload: _autoPreload, ...flat } = normalized;

        // onConnectWallet rides the stable proxy — update the held ref, never the registered handler.
        // Capture a PRESENCE flip (absent<->present) so a toggle forces a re-run below; a pure identity
        // swap (present->present) leaves connectPresenceChanged false and stays rewire-free.
        let connectPresenceChanged = false;
        if ("onConnectWallet" in flat) {
            const had = this._userConnect !== undefined;
            this._userConnect = flat.onConnectWallet;
            connectPresenceChanged = had !== (this._userConnect !== undefined);
            delete (flat as Record<string, unknown>).onConnectWallet;
        }

        // generateFeeSponsorshipGrant rides the stable grant proxy exactly like onConnectWallet — update the
        // held signer ref, never the registered proxy. Capture a PRESENCE flip (absent<->present) so a toggle
        // forces the re-run below (which re-bakes hasFeeSponsorship into the widget); a pure identity swap
        // (present->present) leaves grantPresenceChanged false and stays rewire-free.
        let grantPresenceChanged = false;
        if ("generateFeeSponsorshipGrant" in flat) {
            const had = this._userGenerateGrant !== undefined;
            this._userGenerateGrant = flat.generateFeeSponsorshipGrant as ((p: string) => Promise<string>) | undefined;
            grantPresenceChanged = had !== (this._userGenerateGrant !== undefined);
            delete (flat as Record<string, unknown>).generateFeeSponsorshipGrant;
        }

        const roleChanged =
            "owner" in flat ||
            "funders" in flat ||
            "withdrawFunder" in flat ||
            connectPresenceChanged ||
            grantPresenceChanged;

        // Build a validated candidate and commit only on success — mirror the constructor's validate-then-assign
        // ordering so a rejected update throws to the caller while this._config retains the last-valid config.
        // The shallow merge means distinct flat keys never clobber; a cleared required field is
        // re-defaulted below (applyRequiredDefaults) and injected bridges are preserved (absent from `flat`).
        const candidate = mergeParams(this._config, flat as Partial<StoredConfig>); // null in flat deletes the key
        applyRequiredDefaults(candidate); // re-bake outputs if a clear left it absent (required field)
        validateParams(candidate as PaymentsWidgetSDKParams);
        const validatedOverrides = validateOverrides(candidate.dangerousOverrides ?? {});
        this._config = candidate;

        // Re-assert the proxy invariants on the freshly-committed config: each proxy is present iff a real
        // handler exists. Both onConnectWallet and generateFeeSponsorshipGrant ride their stable proxy, so a
        // later swap needs no re-wire; a presence flip is reconciled here (mergeParams carried the stale proxy
        // from _config, so removal must delete it). Runs BEFORE the !isInitialized return so it holds even with
        // no live widget yet.
        if (this._userConnect) this._config.onConnectWallet = this._connectProxy;
        else delete this._config.onConnectWallet;
        if (this._userGenerateGrant) this._config.generateFeeSponsorshipGrant = this._grantProxy;
        else delete this._config.generateFeeSponsorshipGrant;

        if (!isInitialized(this._ctx)) return; // no live widget yet → applies on the next open/warm

        // A widget-domain override changes the iframe's ORIGIN, so the live iframe cannot be reused — the
        // pipeline must rebuild it (iframe.ts tears down the old-origin iframe on a mismatch). Detect it by
        // recomputing the origin from the just-committed overrides and comparing to the stored one.
        const nextOrigin = computeWindowOrigin(validatedOverrides);
        const originChanged = nextOrigin !== getWindowOrigin(this._ctx);

        if (roleChanged || originChanged) {
            // Reset readiness SYNCHRONOUSLY (before the pipeline's async wallet-resolution await) so a
            // `updateConfig(...); ready()` sequence can't resolve against the stale pre-rebuild snapshot
            // (ready() short-circuits on isReady). The pipeline also resets isReady when it builds the new
            // iframe, but that lands after the await — too late for a synchronous ready() read.
            if (originChanged) ctxSetReactiveState({ isReady: false }, this._ctx);
            // Re-wire handlers + re-run wallet resolution (rebuilding the iframe on an origin change) under
            // the generation guard.
            runPipeline(this._config, { show: false }, this._ctx).catch((err) => this._reportError(err, "resolution"));
        } else if (Object.keys(flat).length > 0) {
            // Serializable-only change → push the diff without re-resolving wallets. `flat` still carries
            // its nulls (a cleared key); neither mergeParams nor applyRequiredDefaults mutate it.
            this._pushParamUpdate(flat as Partial<StoredConfig>);
        }
        // A connect-only change updated the proxy ref above; there is nothing to push.
    };

    // ====================================================================
    // Events
    // ====================================================================

    on = <E extends HallidayEvent>(event: E, handler: HallidayEventHandler<E>): (() => void) => {
        this._listeners[event].add(handler as (arg?: unknown) => void);
        return () => this.off(event, handler);
    };

    off = <E extends HallidayEvent>(event: E, handler: HallidayEventHandler<E>): void => {
        this._listeners[event].delete(handler as (arg?: unknown) => void);
    };

    // ====================================================================
    // @internal — power the React bindings
    // ====================================================================

    /** @internal Warm a hidden iframe so the next open is instant. Idempotent (the generation guard ensures
     *  at most one iframe). Runs automatically after construction; a `preload()` after `destroy()` rebuilds. */
    preload = (): void => {
        this._lastSource = "preload";
        this._gatedEffect(() => {
            this._beginLoadCycle();
            runPipeline(this._config, { show: false }, this._ctx).catch((err) => this._reportError(err, "preload"));
        });
    };

    /** @internal Subscribe to reactive-state changes; returns an unsubscribe fn (useSyncExternalStore). */
    subscribe = (listener: () => void): (() => void) => ctxSubscribe(listener, this._ctx);

    /** @internal Cached, referentially-stable snapshot of reactive state. */
    getSnapshot = (): HallidaySnapshot => ctxGetSnapshot(this._ctx);

    // ====================================================================
    // State getters (mirror the snapshot)
    // ====================================================================

    get isReady(): boolean {
        return ctxGetSnapshot(this._ctx).isReady;
    }

    get isOpen(): boolean {
        return ctxGetSnapshot(this._ctx).isOpen;
    }

    // ====================================================================
    // Internals
    // ====================================================================

    /** Stable host-connect proxy — always calls the current `onConnectWallet`. */
    private _connectProxy = (): void => {
        this._userConnect?.();
    };

    /** Stable host signer proxy — always calls the current `generateFeeSponsorshipGrant` (signs a payload → signature). */
    private _grantProxy = (payload: string): Promise<string> => {
        if (!this._userGenerateGrant) return Promise.reject(new Error("No generateFeeSponsorshipGrant provided"));
        return this._userGenerateGrant(payload);
    };

    /** Run an effecting action through the DOM-ready gate, registering lazily on first DOM touch. */
    private _gatedEffect = (effect: () => void): void => {
        onDomReady(() => {
            this._registerLazily();
            effect();
        });
    };

    private _registerLazily = (): void => {
        if (this._registered) return;
        this._registered = true;
        registerInstance(this._registryKey(), this);
    };

    /** The single resolved per-instance key (also the DOM-id namespace), fixed at construction. Unifying
     *  on `ctx.key` keeps the registry key and DOM ids in lock-step — and never drifts if a later
     *  `updateConfig` changes `targetElementId`. */
    private _registryKey = (): string => this._ctx.key;

    /** Withdrawal inputs: explicit `withdrawal.inputs` (honored even if empty), else `deposit.outputs` minus fiat at open time. */
    private _resolveWithdrawInputs = (): string[] => {
        const explicit = this._config.withdrawInputs as string[] | undefined;
        if (explicit !== undefined) return explicit; // honor explicit list, including empty (no-filter)
        const outputs = (this._config.outputs as string[] | undefined) ?? [];
        // Exclude fiat: keep only chain-scoped assets (a ":" past position 0), mirroring the legacy default.
        return outputs.filter((asset) => asset.indexOf(":") > 0);
    };

    private _maybeCreateSkeleton = (): void => {
        const targetElementId = this._config.targetElementId;
        if (typeof targetElementId !== "string" || isPreloaded(this._ctx)) return;
        const targetElement = document.getElementById(targetElementId);
        if (targetElement) {
            createEmbedSkeleton(
                targetElement,
                this._config.customStyles?.backgroundColor,
                this._config.customStyles,
                this._ctx,
            );
        }
    };

    /**
     * Push the resolved diff of a serializable-only `updateConfig` to the live widget, without re-resolving
     * wallets. The wire carries ONLY the keys that changed (from `flat`), each resolved against the
     * just-committed `this._config`: still-present → its committed value (a cleared-but-re-defaulted `outputs`
     * rides out as the DEFAULT here); now-absent (an optional field that was cleared) → an explicit `null`, so
     * the widget's own `mergeParams` DELETES it live. `this._config` is null-free, so no `null` ever leaks
     * except an intentional clear.
     */
    private _pushParamUpdate = (flat: Partial<StoredConfig>): void => {
        const ctx = this._ctx;
        const windowOrigin = getWindowOrigin(ctx) ?? "";
        const cfg = this._config;

        // `dangerousOverrides` types as `DangerousOverrides | undefined` and `flatRest` stays a typed flat
        // partial — no whole-value bag cast at the seam.
        const { dangerousOverrides, ...flatRest } = flat;
        const diff: Record<string, unknown> = {};
        // Resolve each changed key against the just-committed config. `cfg` is the typed `StoredConfig`, so
        // indexing it by a runtime string needs one narrow cast at the point of use (the permitted
        // `noUncheckedIndexedAccess` dynamic-key carve-out). A now-absent key (a cleared optional field)
        // becomes an explicit null so the widget's own `mergeParams` DELETES it live.
        for (const key of Object.keys(flatRest)) {
            diff[key] = key in cfg ? (cfg as Record<string, unknown>)[key] : null;
        }
        // Override keys reach the widget under their remapped short names (never the raw `dangerously*` bag).
        if (dangerousOverrides) {
            const short = remapOverrides(validateOverrides(dangerousOverrides));
            for (const [key, value] of Object.entries(short)) {
                if (value !== undefined) diff[key] = value;
            }
        }

        const serializable = stripFunctions(diff); // keeps null — the clear signal
        // Keep lastMergedConfig in lockstep with the widget's post-merge state (delete-on-null → null-free).
        mergeAndStoreConfig(serializable, ctx);

        if (isPreloaded(ctx)) {
            sendUpdateParams(getContentWindow(ctx), serializable, windowOrigin);
        } else {
            // Buffer until ready, flushed by the pipeline's onReady wrapper. The accumulation deliberately
            // PRESERVES nulls (raw spread, NOT mergeParams): a clear issued before WIDGET_READY must still
            // reach the widget as a null on flush. A delete-on-null merge here would silently drop the
            // pre-ready clear (do NOT "simplify" this to mergeParams).
            const prev = getState(ctx).pendingUpdateParams;
            const config = prev ? { ...prev.config, ...serializable } : serializable;
            setState({ pendingUpdateParams: { config, windowOrigin } }, ctx);
        }
    };

    // ---- ready() machinery ----

    /** Begin (or re-arm after a failure) the readiness cycle for a genuine (re)load. */
    private _beginLoadCycle = (): void => {
        if (this._ready && !this._readyFailed) return; // a pending/resolved cycle is kept
        this._armReadyCycle();
    };

    private _armReadyCycle = (): void => {
        if (this._ready?.timer) clearTimeout(this._ready.timer);
        this._readyFailed = false;
        let resolve!: () => void;
        let reject!: (error: Error) => void;
        const promise = new Promise<void>((res, rej) => {
            resolve = res;
            reject = rej;
        });
        // Swallow the rejection on the internal chain so an un-awaited ready() never logs an unhandled
        // rejection; a consumer's own `ready().catch(...)` is a separate chain and still fires.
        promise.catch(() => {});
        const timer = setTimeout(() => {
            if (this._ready) this._ready.timer = null;
            this._reportError(
                new WidgetLoadError(
                    `Widget did not become ready within ${READY_TIMEOUT_MS / 1000}s.`,
                    WidgetLoadFailureReason.UNKNOWN,
                    TIMEOUT_DIAGNOSTICS,
                ),
                "load",
            );
        }, READY_TIMEOUT_MS);
        this._ready = { promise, resolve, reject, timer };
    };

    // ---- bridges injected into the flat config / context ----

    private _handleWidgetReady = (): void => {
        ctxSetReactiveState({ isReady: true, error: null }, this._ctx);
        if (this._ready) {
            if (this._ready.timer) clearTimeout(this._ready.timer);
            this._ready.timer = null;
            this._ready.resolve();
        }
    };

    private _handleStatus = (status: { type: string; payload: OrderStatus }): void => {
        ctxSetReactiveState({ status }, this._ctx);
        this._emit("status", status);
    };

    private _handleNotifications = (notifications: OrderNotification[]): void => {
        ctxSetReactiveState({ notifications }, this._ctx);
        // State only — no event emit (HallidayEvent stays "status" | "error" | "close").
    };


    private _handleError = (error: Error): void => {
        this._reportError(error);
    };

    private _handleClose = (): void => {
        this._emit("close");
    };

    private _emit = (event: HallidayEvent, arg?: unknown): void => {
        for (const handler of [...this._listeners[event]]) handler(arg);
    };

    /**
     * Surface a runtime/async failure: tag with `source`, store it, reject `ready()`, emit `"error"`,
     * and dev-warn when no channel is wired. Never throws.
     */
    private _reportError = (error: unknown, sourceHint?: HallidayErrorSource): void => {
        const err = error instanceof Error ? error : new Error(String(error));
        const source: HallidayErrorSource = err instanceof WidgetLoadError ? "load" : (sourceHint ?? this._lastSource);
        const tagged = err as HallidayRuntimeError;
        (tagged as { source: HallidayErrorSource }).source = source;

        ctxSetReactiveState({ error: tagged }, this._ctx);
        if (this._ready) {
            if (this._ready.timer) clearTimeout(this._ready.timer);
            this._ready.timer = null;
            this._readyFailed = true;
            this._ready.reject(tagged);
        }
        this._emit("error", tagged);

        // No-missed-errors guarantee: dev-only console.error when nothing is listening.
        if (!this._readyAwaited && this._listeners.error.size === 0) {
            devError(
                "[HallidayPayments] Unhandled widget error (no `error` listener and `ready()` not awaited):",
                tagged,
            );
        }
    };
}

export default HallidayPayments;
